<template>
  <v-card class="new-card" rounded="lg" flat>
    <div class="d-flex justify-space-between align-center">
      <h2 class="idgjald-heading">
        {{ t('idgjaldarsida_manudum') }}
      </h2>
      <a class="see-all-link" href="/idgjold">
        {{ t('idgjaldarsida_SeeAll') }}
      </a>
    </div>

    <v-data-table
      :headers="headers"
      :items="limitedItems"
      :loading="idgjold === null"
      loading-text="Sæki gögn..."
      no-data-text="Engin gögn fundust"
      :mobile-breakpoint="0"
      class="idgjald-data-table"
      hide-default-footer
      hover
    >
      <template #item.launaGreidandi="{ item }">
        {{ item.launaGreidandi }}
        <div class="date-text">
          {{ formatDagsGreitt(item.dagsGreitt) }}
        </div>
      </template>

      <template #item.idgjald="{ item }">
        {{ currency(item.idgjald) }} kr
      </template>

      <template #item.motframlag="{ item }">
        {{ currency(item.motframlag) }} kr
      </template>

      <template #item.samtals="{ item }">
        {{ currency(item.samtals) }} kr
      </template>
    </v-data-table>
  </v-card>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTranslationStore } from '@/stores/useTranslationStore'
import { formatDate } from '@/filters/date'
import { useCurrency } from '@/composables/useCurrency'

const { currency } = useCurrency()

const props = defineProps({
  idgjold: {
    type: Array,
    default: () => [],
  }
})

useTranslationStore() // kept if you rely on global translations elsewhere
const { t, locale } = useI18n()

const headers = computed(() => [
  { title: t('subpage_idgjold_table_header4'), key: 'launaGreidandi', sortable: false },
  { title: t('subpage_idgjold_table_header5'), key: 'idgjald', align: 'end', sortable: false },
  { title: t('subpage_idgjold_table_header6'), key: 'motframlag', align: 'end', sortable: false },
  { title: t('subpage_idgjold_table_header7'), key: 'vextir', align: 'end', sortable: false },
  { title: t('subpage_idgjold_table_header8'), key: 'samtals', align: 'end', sortable: false },
])

// Exclude negatives, then take the first 5
const limitedItems = computed(() =>
  (props.idgjold ?? [])
    .filter(row => (row?.samtals ?? 0) >= 0)
    .slice(0, 5)
)

function formatDagsGreitt(value) {
  return formatDate(value, locale.value)
}
</script>

<style scoped lang="scss">
@use "@/scss/settings/variables" as var;
@use "@/scss/settings/media-queries" as mq;

.new-card {
  border: 1px solid var.$color-border !important;
  box-shadow: none !important;
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease !important;

  &:hover {
    border-color: color-mix(in srgb, var.$color-border 70%, #000 30%) !important;
    box-shadow: 0 1px 0 rgba(0,0,0,.02), 0 0 0 3px rgba(0,0,0,.02) inset !important;
  }
}

:global(body.dark-mode) .new-card {
  border-color: var.$color-border-dark !important;
  
  &:hover {
    border-color: color-mix(in srgb, var.$color-border-dark 70%, #fff 30%) !important;
    box-shadow: 0 1px 0 rgba(255,255,255,.03), 0 0 0 3px rgba(255,255,255,.03) inset !important;
  }
}

.idgjald-heading {
  font-size: var.$font-size-lg;
  font-weight: var.$font-weight-bold;
  color: var.$color-text;
  
  @include mq.mobile {
    font-size: 18px;
  }
}

.date-text {
  font-size: var.$font-size-sm;
  color: var.$color-text-muted;
  margin-top: 2px;
}

.idgjald-data-table {
  padding: 0 !important;
}

.see-all-link {
  font-size: var.$font-size-sm;
  font-weight: var.$font-weight-bold;
  color: var.$color-text;
  text-decoration: none;
  position: relative;
  cursor: pointer;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -2px;
    height: 2px;
    width: 100%;
    background-color: var.$color-text;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: scaleX(1);
  }
}

:deep(.v-table) {
  .v-table__wrapper table tbody tr {
    border-bottom: 1px solid var.$color-border;
  }

  .v-table__wrapper table thead tr th {
    border-bottom: none;
    padding: 0;
  }

  .v-table__wrapper table tbody tr td {
    padding: 0;
  }

  .v-table__wrapper table tbody tr:last-child {
    border-bottom: none;
  }

  .v-table__wrapper table tbody tr:first-child {
    border-top: none;
  }

  td {
    padding-top: 12px;
    padding-bottom: 12px;
    padding-left: var.$spacing-sm;
    padding-right: var.$spacing-sm;
  }

  thead th {
    color: var.$color-text-muted;
    font-weight: var.$font-weight-bold !important;
    font-size: var.$font-size-sm;
  }
  
  @include mq.mobile {
    .v-table__wrapper {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
    
    table {
      min-width: 600px;
      font-size: 14px;
    }
    
    thead th {
      font-size: 12px;
      white-space: nowrap;
    }
    
    td {
      padding-top: 8px;
      padding-bottom: 8px;
      padding-left: 4px;
      padding-right: 4px;
      font-size: 14px;
    }
    
    .date-text {
      font-size: 12px;
    }
  }
}

.v-data-table-footer__divider {
  display: none !important;
}
</style>
